import Vue from "vue";
import VueRouter from "vue-router";
import VueMeta from "vue-meta";
import qs from "qs";
import store from "@/state/store";
import routes from "./routes";

Vue.use(VueRouter);
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: "page"
});

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: "history",
  parseQuery: query => {
    return qs.parse(query);
  },
  stringifyQuery(query) {
    let result = qs.stringify(query, { encode: false });

    return result ? "?" + result : "";
  }

  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  // scrollBehavior(to, from, savedPosition) {
  //   if (savedPosition) {
  //     return savedPosition
  //   } else {
  //     return { x: 0, y: 0 }
  //   }
  // },
});

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  // Check if auth is required on this route
  // (including nested routes).
  const authRequired = routeTo.matched.some(route => route.meta.authRequired);

  // If auth isn't required for the route, just continue.
  if (!authRequired) return next();

  // Check route-level permission after auth is confirmed below.
  function checkPermission() {
    const requiredPermission = routeTo.meta.permission;
    if (!requiredPermission) return true;
    const tradeUser = store.state['oauth2'] && store.state['oauth2'].user && store.state['oauth2'].user.trade_user;
    const userPermissions = (tradeUser && tradeUser.permissions) || [];
    return userPermissions.includes(requiredPermission);
  }

  function isActive() {
    const tradeUser = store.state['oauth2'] && store.state['oauth2'].user && store.state['oauth2'].user.trade_user;
    return tradeUser ? tradeUser.is_active !== false : true;
  }

  if (process.env.VUE_APP_DEFAULT_AUTH === "firebase") {
    // If auth is required and the user is logged in...
    if (store.getters["auth/loggedIn"]) {
      // Validate the local user token...
      return store.dispatch("auth/validate").then(validUser => {
        // Then continue if the token still represents a valid user,
        // otherwise redirect to login.
        if (!validUser) return redirectToLogin();
        if (!isActive()) return next('/deactivated');
        if (!checkPermission()) return next('/403');
        next();
      });
    }

    // If auth is required and the user is NOT currently logged in,
    // redirect to login.
    redirectToLogin();

    // eslint-disable-next-line no-unused-vars
    // eslint-disable-next-line no-inner-declarations
    function redirectToLogin() {
      // Pass the original route to the login component
      next({ path: "/login", query: { redirectFrom: routeTo.fullPath } });
    }
  } else {
    // const publicPages = ['/login', '/register', '/forgot-password', '/verify_email/resend', '/verify_email/success'];
    // const authpage = !publicPages.includes(routeTo.path);
    let loggeduser = null;
    try {
      loggeduser = JSON.parse(localStorage.getItem("user"));
    } catch (e) {
      //
    }

    // If auth is required and the user is logged in...
    if (loggeduser) {
      const tradeUser = store.state['oauth2'] && store.state['oauth2'].user && store.state['oauth2'].user.trade_user;

      if (process.env.VUE_APP_DEFAULT_AUTH === "oauth2") {
        // If trade_user is not yet loaded (first navigation after login), wait for
        // validate to complete so permission checks have the full profile.
        if (!tradeUser) {
          return store.dispatch("oauth2/validate").then(validUser => {
            if (!validUser) return next({ path: "/login", query: { redirectFrom: routeTo.fullPath } });
            if (!isActive()) return next('/deactivated');
            if (!checkPermission()) return next('/403');
            return next();
          });
        }
        // trade_user already in store — check synchronously, refresh token in background.
        if (!isActive()) return next('/deactivated');
        if (!checkPermission()) return next('/403');
        store.dispatch("oauth2/validate").then(validUser => {
          if (!validUser) return next({ path: "/login", query: { redirectFrom: routeTo.fullPath } });
        });
        return next();
      } else {
        if (!isActive()) return next('/deactivated');
        if (!checkPermission()) return next('/403');
        return next();
      }
    }

    next({ path: "/login", query: { redirectFrom: routeTo.fullPath } });
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  // If we reach this point, continue resolving the route.
  next();
});

export default router;
